/**
 * Migration Cookie Utility
 * Manages domain-specific migration cookies for bobcat (aux) and avant
 */
const MIGRATION_KEYS = ['migration_aux', 'migration_avant'] as const;

export interface MigrationCookie {
    Domain: 'aux' | 'avant';
    isMigrated: boolean;
    Registered: boolean;
}

/**
 * Get the current domain type based on hostname.
 * Must recognize every deployed customer host where migration runs; otherwise setCurrentDomainMigrationCookie
 * no-ops and users see the migration page again after completing migration.
 */
export const getDomainType = (): 'aux' | 'avant' | null => {
    const hostname = window.location.hostname.toLowerCase();

    // Debug logging
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

    if (isLocalhost) {
        const env = (process.env.REACT_APP_MIGRATION_DOMAIN || '').toLowerCase();
        if (env === 'aux' || env === 'avant') {
            console.log('[Migration] Domain type (localhost with env var)', { hostname, env });
            return env;
        }
        console.warn(
            '[Migration] Localhost without REACT_APP_MIGRATION_DOMAIN - set env var for proper cookie domain handling',
            {
                hostname,
                env,
            },
        );
        return null;
    }

    if (!hostname.includes('servicehqtrs.com')) {
        console.warn('[Migration] Hostname not recognized', { hostname });
        return null;
    }

    // Avant — check before generic `customer-dev` (customer-dev2 contains "customer-dev")
    if (hostname.includes('customer-dev2') || hostname.includes('customer-avant') || hostname.includes('avant-stage')) {
        console.log('[Migration] Domain type (avant)', { hostname });
        return 'avant';
    }

    // Aux (bobcat) customer portals
    if (
        hostname.includes('customer-dev') ||
        hostname.includes('customer-stage') ||
        hostname.includes('customer-qa') ||
        hostname.includes('customer-uat')
    ) {
        console.log('[Migration] Domain type (aux)', { hostname });
        return 'aux';
    }

    // Other customer-* hosts on this domain (prod / future envs)
    if (hostname.startsWith('customer-')) {
        console.log('[Migration] Domain type (aux - default)', { hostname });
        return 'aux';
    }

    console.warn('[Migration] Hostname mapping failed - no domain type matched', { hostname });
    return null;
};

/**
 * Get migration cookie from localStorage for a specific domain
 */
export const getMigrationCookie = (domain: 'aux' | 'avant'): MigrationCookie | null => {
    try {
        const cookieKey = `migration_${domain}`;
        const cookie = localStorage.getItem(cookieKey);

        if (cookie) {
            return JSON.parse(cookie);
        }
    } catch (error) {
        console.error('Error reading migration cookie:', error);
    }

    return null;
};

/**
 * Set migration cookie in localStorage for a specific domain
 */
export const setMigrationCookie = (domain: 'aux' | 'avant', cookie: MigrationCookie): void => {
    try {
        const cookieKey = `migration_${domain}`;
        localStorage.setItem(cookieKey, JSON.stringify(cookie));
    } catch (error) {
        console.error('Error setting migration cookie:', error);
    }
};

/**
 * Get migration cookie for current domain
 */
export const getCurrentDomainMigrationCookie = (): MigrationCookie | null => {
    const domainType = getDomainType();

    if (!domainType) {
        return null;
    }

    return getMigrationCookie(domainType);
};

/**
 * Set migration cookie for current domain
 */
export const setCurrentDomainMigrationCookie = (cookie: Partial<MigrationCookie>): void => {
    const domainType = getDomainType();

    if (!domainType) {
        // Last resort so migration completion is never silently dropped (e.g. misconfigured host).
        console.warn(
            '[migration] Host did not map to aux/avant; storing migration flag on both keys. Set REACT_APP_MIGRATION_DOMAIN for localhost.',
        );
        (['aux', 'avant'] as const).forEach(d => {
            const currentCookie = getMigrationCookie(d) || {
                Domain: d,
                isMigrated: false,
                Registered: false,
            };
            const updatedCookie: MigrationCookie = {
                ...currentCookie,
                ...cookie,
                Domain: d,
            };
            setMigrationCookie(d, updatedCookie);
            console.log('[Migration] Cookie set (fallback)', {
                domain: d,
                cookie: updatedCookie,
            });
        });
        return;
    }

    const currentCookie = getMigrationCookie(domainType) || {
        Domain: domainType,
        isMigrated: false,
        Registered: false,
    };

    const updatedCookie: MigrationCookie = {
        ...currentCookie,
        ...cookie,
        Domain: domainType,
    };

    setMigrationCookie(domainType, updatedCookie);
    console.log('[Migration] Cookie set', {
        domain: domainType,
        cookie: updatedCookie,
    });
};

/**
 * Check if user has already migrated for current domain
 */
export const isUserAlreadyMigrated = (): boolean => {
    const domainType = getDomainType();
    const current = getCurrentDomainMigrationCookie();

    if (current?.isMigrated) {
        console.log('[Migration] User already migrated - current domain migration cookie found', {
            domain: domainType,
            isMigrated: current.isMigrated,
        });
        return true;
    }

    // If hostname mapping missed but a key was written (e.g. dual-write fallback), still skip migration.
    const auxMigrated = getMigrationCookie('aux')?.isMigrated;
    const avantMigrated = getMigrationCookie('avant')?.isMigrated;
    const fallbackMigrated = Boolean(auxMigrated || avantMigrated);

    if (fallbackMigrated) {
        console.log('[Migration] User already migrated - fallback migration cookie found', {
            domain: domainType,
            aux: auxMigrated,
            avant: avantMigrated,
        });
    } else {
        console.log('[Migration] No migration cookies found', {
            domain: domainType,
            domainType,
        });
    }

    return fallbackMigrated;
};

/**
 * Clear migration marker for the current host (e.g. API says user must complete migration again).
 */
export const clearMigrationCookiesForCurrentHost = (): void => {
    const domainType = getDomainType();
    if (domainType === 'aux') {
        localStorage.removeItem('migration_aux');
    } else if (domainType === 'avant') {
        localStorage.removeItem('migration_avant');
    } else {
        localStorage.removeItem('migration_aux');
        localStorage.removeItem('migration_avant');
    }
};

/**
 * Clear localStorage but preserve migration cookies so that after logout user is sent to login (not update_portal).
 */
export const clearStoragePreservingMigrationCookies = (): void => {
    const saved: Record<string, string> = {};
    MIGRATION_KEYS.forEach(key => {
        const v = localStorage.getItem(key);
        if (v) saved[key] = v;
    });
    localStorage.clear();
    Object.entries(saved).forEach(([key, value]) => localStorage.setItem(key, value));
};
